import { filter, first, map, shareReplay, tap } from 'rxjs/operators';
import { Injectable } from "@angular/core";
import { IDocumentResponse, IDocumentType } from "../models/document";
import { Document } from "../models/file-upload.model";
import { DocumentEndpoint } from "./document-endpoint.service";
import { UtilitiesString } from "./utilities.string";
import { DataAction, StateRepository } from '@angular-ru/ngxs/decorators';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { Selector, State, Store } from '@ngxs/store';
import { produce } from 'immer';
import { AppStateModel } from './+redux/redux.models';
import { Utilities } from './utilities';

@StateRepository()
@State<DocumentStateModel>({
    name: 'documentState',
    defaults: {

    }
})
@Injectable()
export class DocumentService extends NgxsDataRepository<DocumentStateModel> {
    loadingDocumentTypes$: any;

    @Selector()
    static documentTypesSelector(state: DocumentStateModel) {
        return state.documentTypes;
    }

    get documentTypes() {
        return this.store.selectSnapshot(DocumentService.documentTypesSelector);
    }

    documents: Document[] = [];

    constructor(
        private readonly documentEndpoint: DocumentEndpoint,
        private readonly store: Store
    ) {
        super();
    }

    public async UploadDocument(tenantId: string, memberId: string, input: FormData): Promise<Document[]> {
        const newDocuments = await this.documentEndpoint.uploadDocEndpoint<Document[]>(tenantId, memberId, input).toPromise();

        return newDocuments;
    }

    public async DownloadDocument(tenantId: string, memberId: string, docId: string): Promise<any> {
        return await this.documentEndpoint.downloadDocEndpoint<any>(tenantId, memberId, docId).toPromise();
    }

    public async getFiles<T extends any>(tenantId: string, memberId: string, documentTypeMetadataFilter: string = null): Promise<T> {
        return this.documentEndpoint.getFiles<T>(tenantId, memberId, documentTypeMetadataFilter).toPromise();
    }

    public async getDocument<T extends any>(tenantId: string, documentId: number): Promise<T> {
        return await this.documentEndpoint.getDocument<T>(tenantId, documentId).toPromise();
    }

    async getDocumentTypesFilterByShowInUI(): Promise<IDocumentType[]> {
        return (await this.getDocumentTypeAfterSort()).filter(type => type.showInUI);
    }

    async getDocumentTypes(): Promise<IDocumentType[]> {
        return (await this.getDocumentTypeAfterSort());
    }

    static removeOlderVersions(documents: IDocumentType[]) {
        return documents.reduce<IDocumentType[]>((prev: IDocumentType[], cur: IDocumentType, index: number, array: IDocumentType[]) => {
            const existingIndex = array.findIndex(item => item.type === cur.type);
            if (existingIndex === -1) {
                array.push(cur);
            } else if (UtilitiesString.compareStringWithNumbers(array[existingIndex].version, cur.version)) {
                array.splice(existingIndex, 1, cur)
            }
            return array;
        }, []);
    }

    async getAllDocumentTypes(): Promise<IDocumentType[]> {
        let result = await this.getDocumentType();

        return result;
    }

    private async getDocumentTypeAfterSort(): Promise<IDocumentType[]> {
        let result = await this.getDocumentType();
        result = DocumentService.removeOlderVersions(result);
        return result.sort((a, b) => a.label === b.label ? 0 : (a.label < b.label ? -1 : 1));
    }

    @DataAction()
    async getDocumentType(): Promise<IDocumentType[]> {

        if (!this.loadingDocumentTypes$ && !this.store.selectSnapshot<IDocumentType[]>(DocumentService.documentTypesSelector)) {
            this.loadingDocumentTypes$ = this.documentEndpoint.getDocumentTypeEndpoint().pipe(first());

            const result = (await this.loadingDocumentTypes$.toPromise()).map(item => {
                item.label = `${item.description} ${item.version}`;
                return item;
            });

            this.ctx.setState(produce(this.ctx.getState(), (draft: DocumentStateModel) => {
                draft.documentTypes = result;
            }));
        }

        return this.store.select<IDocumentType[]>(DocumentService.documentTypesSelector).pipe(filter(_ => !!_), first(), map(_ => Utilities.cloneHard<IDocumentType[]>(_))).toPromise();
    }

    public async UpdateDocument(tenantId: string, file: any): Promise<any> {
        return await this.documentEndpoint.updateDocEndpoint(tenantId, file).toPromise();
    }

    public async DeleteDocument(tenantId: string, docId: string): Promise<any> {
        return await this.documentEndpoint.deleteDocEndpoint(tenantId, docId).toPromise();
    }

    public async latestDocumentByType(tenantId: string, memberId: string, type: string, download?: boolean): Promise<IDocumentResponse> {
        return await this.documentEndpoint.latestDocumentByType(tenantId, memberId, type, download);
    }

    async documentsByType(tenantId: string, memberId: string, type: string): Promise<IDocumentResponse[]> {
        return await this.documentEndpoint.documentsByType(tenantId, memberId, type);
    }
}

export interface DocumentStateModel {
    documentTypes?: IDocumentType[];
}
